/**
 * Home — "Frost" dashboard (D88).
 * Desktop: greeting, big total and quick actions on the pastel sky; chart and asset tiles below;
 * the vault card and recent activity in a right-hand column.
 * Phone/tablet: a frosted total panel, then the chart and asset rows, stacked.
 * Totals, values and the chart come from live prices (Phase 8) in the chosen display currency.
 * Anything without a fresh price shows "—" — never an invented number.
 * Step 5 (D80): labelled actions, time-of-day greeting, hide-balances eye, asset rows with the
 * 24 h price change, placeholders while loading.
 */
import type { Balance, ChartRange, PortfolioSummary } from "@custos/api-client";
import {
  Badge,
  EmptyState,
  Notice,
  Segmented,
  Skeleton,
  Spot,
  cn,
  formatAmount,
  formatBps,
  formatDateTime,
  formatFiat,
  isZeroAmount,
  truncateMiddle,
} from "@custos/ui";
import {
  ArrowDownLeft,
  ArrowLeftRight,
  ArrowUpRight,
  Eye,
  EyeOff,
  Lock,
  Plus,
  ShieldCheck,
} from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router";
import { AssetIcon } from "../components/bits";
import { PortfolioChart } from "../components/PortfolioChart";
import { KIND_LABEL, KindIcon, status } from "./Activity";
import { VaultHomeTab } from "./Vault";
import { mergeActivity, type ActivityItem } from "../lib/activity";
import { findChain, networkLabel } from "../lib/chains";
import { errorMessage } from "../lib/errors";
import { greeting } from "../lib/greeting";
import { useMask, usePrefs } from "../lib/prefs";
import {
  useBalances,
  useConfig,
  useDeposits,
  usePortfolio,
  usePortfolioHistory,
  useSwaps,
  useVaultBalances,
  useWithdrawals,
} from "../lib/queries";
import { useVaultSession } from "../lib/vault-ui";

const ACTIONS = [
  { to: "/withdraw", label: "Withdraw", icon: ArrowUpRight },
  { to: "/deposit", label: "Deposit", icon: ArrowDownLeft },
  { to: "/swap", label: "Swap", icon: ArrowLeftRight },
];

const RANGES: ChartRange[] = ["1d", "1w", "1m", "1y"];

/** "$1,501.91" with the cents smaller, as a light, tightly spaced display number. */
function BigMoney({ text }: { text: string }) {
  const dot = text.lastIndexOf(".");
  if (dot < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, dot)}
      <span className="text-[0.55em] text-muted">{text.slice(dot)}</span>
    </>
  );
}

export function HomePage() {
  const config = useConfig();
  const balances = useBalances();
  const deposits = useDeposits();
  const currency = usePrefs((s) => s.currency);
  const [layer, setLayer] = useState<"account" | "vault">("account");
  const [range, setRange] = useState<ChartRange>("1d");
  const portfolio = usePortfolio(currency);
  const history = usePortfolioHistory(currency, range);
  const summary = portfolio.data;
  const noPrices = summary !== undefined && !summary.complete && summary.total === "0.00";
  const [showAll, setShowAll] = useState(false);

  const all = balances.data ?? [];
  const funded = all.filter((b) => !isZeroAmount(b.balance));
  const visible = showAll ? all : funded;
  const pending = (deposits.data ?? []).filter((d) => d.status === "DETECTED" || d.status === "CONFIRMING");
  const hide = usePrefs((s) => s.hideBalances);
  const setHide = usePrefs((s) => s.setHideBalances);
  const mask = useMask();
  // Rows sorted by value (unknown values last), like leading wallets.
  const sorted = [...visible].sort(
    (a, b) => Number(valueOf(summary, b) ?? -1) - Number(valueOf(summary, a) ?? -1),
  );

  return (
    <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start lg:gap-6">
      <div className="flex min-w-0 flex-col gap-5">
        {/* ── Total + quick actions: a frosted panel on phones, open on the sky on PCs ── */}
        <section
          aria-label="Wallet"
          className="glass animate-rise flex flex-col gap-6 rounded-[32px] p-5 text-center sm:p-8 lg:flex-row lg:flex-wrap lg:items-end lg:justify-between lg:rounded-none lg:border-0 lg:bg-transparent lg:p-0 lg:text-left lg:shadow-none lg:backdrop-blur-none"
        >
          <div>
            <div className="flex items-center justify-center gap-1 lg:justify-start">
              <h1 className="text-base font-normal tracking-normal text-muted">{greeting()}</h1>
              <button
                type="button"
                onClick={() => setHide(!hide)}
                aria-label={hide ? "Show balances" : "Hide balances"}
                aria-pressed={hide}
                title={hide ? "Show balances" : "Hide balances"}
                className="inline-flex size-11 items-center justify-center rounded-full text-muted hover:bg-hover"
                data-testid="toggle-balances"
              >
                {hide ? <EyeOff className="size-5" aria-hidden /> : <Eye className="size-5" aria-hidden />}
              </button>
            </div>
            {portfolio.isPending ? (
              <span className="mx-auto mt-1 block h-14 w-56 animate-pulse rounded-2xl bg-hover sm:h-16 lg:mx-0" />
            ) : (
              <p
                className="text-6xl leading-none font-light tracking-[-0.05em] text-fg sm:text-7xl lg:text-6xl min-[1440px]:text-7xl"
                data-testid="wallet-total"
              >
                {summary && !noPrices ? <BigMoney text={mask(formatFiat(summary.total, currency))} /> : "—"}
              </p>
            )}
            {summary && noPrices ? (
              <span className="mt-3 inline-flex rounded-full bg-surface/70 px-3 py-1 text-sm text-muted">
                Prices unavailable right now
              </span>
            ) : summary && !summary.complete ? (
              <span className="mt-3 inline-flex rounded-full bg-surface/70 px-3 py-1 text-sm text-muted">
                Some assets have no price yet
              </span>
            ) : (
              history.data?.changeBps != null && (
                <span
                  className={cn(
                    "mt-3 inline-flex rounded-full px-3 py-1 text-sm font-semibold",
                    history.data.changeBps >= 0 ? "bg-success/12 text-success" : "bg-danger/12 text-danger",
                  )}
                  data-testid="wallet-change"
                >
                  {history.data.changeBps >= 0 ? "▲ +" : "▼ "}
                  {formatBps(history.data.changeBps)} · {range}
                </span>
              )
            )}
          </div>

          <nav aria-label="Quick actions" className="flex items-start justify-center gap-2.5 lg:justify-end">
            {ACTIONS.map((a) => (
              <Link
                key={a.to}
                to={a.to}
                aria-label={a.label}
                className="group flex w-16 shrink-0 flex-col items-center gap-1.5"
              >
                <span className="glass-circle inline-flex size-14 items-center justify-center rounded-full transition-transform duration-200 group-active:scale-95">
                  <a.icon className="size-6" aria-hidden />
                </span>
                <span className="text-xs font-medium text-muted" aria-hidden>
                  {a.label}
                </span>
              </Link>
            ))}
            <Link
              to="/deposit"
              aria-label="Add funds"
              className="bg-primary-gradient inline-flex h-14 min-w-20 flex-1 items-center justify-center gap-2 rounded-full px-5 text-base font-semibold shadow-pop transition-transform duration-200 active:scale-95 sm:max-w-44 lg:flex-none"
            >
              <Plus className="size-5" aria-hidden />
              <span className="hidden min-[400px]:inline">Add funds</span>
              <span className="min-[400px]:hidden">Add</span>
            </Link>
          </nav>
        </section>

        {pending.length > 0 && (
          <Notice
            tone="warning"
            title={`${pending.length} deposit${pending.length > 1 ? "s" : ""} confirming`}
          >
            {pending
              .map(
                (d) => `${formatAmount(d.amount)} ${d.asset} (${d.confirmations}/${d.requiredConfirmations})`,
              )
              .join(", ")}{" "}
            — added to your balance once confirmed.
          </Notice>
        )}

        {/* Account value over time (price snapshots every 5 minutes, D53). */}
        <section aria-label="Account value" className="glass animate-rise rounded-[28px] p-4 sm:p-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-fg">Account value</h2>
            <Segmented
              label="Chart range"
              value={range}
              onChange={setRange}
              className="[&>button]:min-h-10 [&>button]:px-3 lg:[&>button]:min-h-8"
              options={RANGES.map((r) => ({ value: r, label: r }))}
            />
          </div>
          <PortfolioChart
            history={
              history.data?.range === range && history.data.currency === currency ? history.data : undefined
            }
            currency={currency}
            className="mt-4 h-40 sm:h-56 lg:h-72"
          />
        </section>

        {/* ── Assets: rows on phones and tablets, tiles on PCs ── */}
        <section aria-label="Assets" className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-base font-semibold text-fg">Assets</h2>
            <Segmented
              label="Wallet layer"
              value={layer}
              onChange={setLayer}
              options={[
                { value: "account", label: "Account" },
                { value: "vault", label: "Vault" },
              ]}
            />
          </div>

          {layer === "vault" ? (
            <VaultHomeTab />
          ) : balances.isPending ? (
            <div className="flex flex-col gap-2">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-[4.5rem] rounded-card" />
              ))}
            </div>
          ) : balances.error ? (
            <Notice tone="danger">{errorMessage(balances.error)}</Notice>
          ) : visible.length === 0 ? (
            <div className="glass rounded-card">
              <EmptyState icon={<Spot name="coins" />} title="No funds yet">
                Deposit testnet coins to get started.{" "}
                <Link to="/deposit" className="text-primary-strong hover:underline">
                  Get a deposit address
                </Link>
              </EmptyState>
            </div>
          ) : (
            <>
              <p className="-mb-1 text-xs text-muted">Custodial — held by the service for you.</p>
              <ul
                className="glass stagger flex flex-col divide-y divide-line overflow-hidden rounded-card lg:grid lg:grid-cols-2 lg:gap-3 lg:divide-y-0 lg:overflow-visible lg:rounded-none lg:border-0 lg:bg-transparent lg:shadow-none lg:backdrop-blur-none xl:grid-cols-3 min-[1400px]:grid-cols-4"
                data-testid="balances"
              >
                {sorted.map((b) => (
                  <AssetRow
                    key={`${b.chain}:${b.asset}`}
                    balance={b}
                    value={valueOf(summary, b)}
                    change={
                      summary?.assets.find((a) => a.chain === b.chain && a.asset === b.asset)?.change24hBps ??
                      null
                    }
                    currency={currency}
                    mask={mask}
                    network={networkLabel(findChain(config.data?.chains ?? [], b.chain), b.chain)}
                  />
                ))}
              </ul>
            </>
          )}

          {layer === "account" && all.length > funded.length && (
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              className="mx-auto min-h-11 rounded-full px-4 text-sm font-medium text-primary-strong hover:bg-hover"
            >
              {showAll ? "Show less" : "See more"}
            </button>
          )}
        </section>
      </div>

      {/* ── PC right-hand column ── */}
      <aside className="hidden flex-col gap-5 lg:flex">
        <VaultCard />
        <RecentActivity />
      </aside>
    </div>
  );
}

/** The self-custody vault as a blue card, with its real state. */
function VaultCard() {
  const vault = useVaultSession();
  const currency = usePrefs((s) => s.currency);
  const mask = useMask();
  const unlocked = vault.status === "unlocked";
  const balances = useVaultBalances(vault.accounts, currency, unlocked);
  const address = vault.accounts[0]?.address;
  return (
    <Link
      to="/vault"
      className="bg-primary-gradient animate-rise flex min-h-44 flex-col justify-between rounded-[28px] p-5 shadow-pop transition-transform duration-200 hover:-translate-y-0.5"
      data-testid="home-vault-card"
    >
      <div className="flex items-center justify-between gap-2 text-sm font-medium">
        <span className="inline-flex items-center gap-2">
          <ShieldCheck className="size-4" aria-hidden /> Vault
        </span>
        {vault.status === "locked" && (
          <span className="inline-flex items-center gap-1 rounded-full bg-white/20 px-2.5 py-0.5 text-xs">
            <Lock className="size-3" aria-hidden /> Locked
          </span>
        )}
        {unlocked && <span className="rounded-full bg-white/20 px-2.5 py-0.5 text-xs">Unlocked</span>}
      </div>
      <p className="my-4 text-3xl font-light tracking-[-0.03em]">
        {unlocked
          ? balances.data
            ? mask(formatFiat(balances.data.total, currency))
            : "—"
          : vault.status === "locked"
            ? "Unlock to see"
            : "Set up your vault"}
      </p>
      <div className="flex items-center justify-between gap-2 text-xs text-white/85">
        <span className="font-mono">{unlocked && address ? truncateMiddle(address) : ""}</span>
        <span>Self-custody</span>
      </div>
    </Link>
  );
}

function RecentActivity() {
  const config = useConfig();
  const deposits = useDeposits();
  const withdrawals = useWithdrawals();
  const swaps = useSwaps();
  const items = useMemo(
    () => mergeActivity(deposits.data ?? [], withdrawals.data ?? [], swaps.data ?? []).slice(0, 6),
    [deposits.data, withdrawals.data, swaps.data],
  );
  const chains = config.data?.chains ?? [];
  return (
    <section aria-label="Recent activity" className="glass animate-rise rounded-[28px] p-4">
      <div className="flex items-center justify-between gap-2 px-1">
        <h2 className="text-base font-semibold text-fg">Activity</h2>
        <Link to="/activity" className="text-sm font-medium text-primary-strong hover:underline">
          See all
        </Link>
      </div>
      {items.length === 0 ? (
        <p className="px-1 py-6 text-center text-sm text-muted">Nothing here yet.</p>
      ) : (
        <ul className="mt-2 flex flex-col divide-y divide-line">
          {items.map((item) => (
            <RecentRow
              key={`${item.kind}-${item.id}`}
              item={item}
              network={networkLabel(findChain(chains, item.chain), item.chain)}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function RecentRow({ item, network }: { item: ActivityItem; network: string }) {
  const mask = useMask();
  const s = status(item);
  const amount =
    item.kind === "swap"
      ? `+${formatAmount(item.data.toAmount, 6)} ${item.data.toAsset}`
      : `${item.kind === "deposit" ? "+" : "−"}${formatAmount(item.amount, 6)} ${item.asset}`;
  return (
    <li className="flex items-center gap-2.5 px-1 py-2.5" title={formatDateTime(item.at)}>
      <KindIcon kind={item.kind} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-fg">{KIND_LABEL[item.kind]}</p>
        <p className="truncate text-xs text-muted">{network}</p>
      </div>
      <div className="flex min-w-0 flex-col items-end gap-0.5">
        <span className="truncate font-mono text-xs text-fg tabular-nums">{mask(amount)}</span>
        <Badge tone={s.tone} title={s.help}>
          {s.label}
        </Badge>
      </div>
    </li>
  );
}

function valueOf(summary: PortfolioSummary | undefined, b: Balance): string | null {
  return summary?.assets.find((a) => a.chain === b.chain && a.asset === b.asset)?.value ?? null;
}

/** 24 h price change chip: green up, red down, muted when unknown. */
function ChangeChip({ bps }: { bps: number | null }) {
  if (bps === null) return <span className="text-xs text-muted">—</span>;
  if (bps === 0)
    return (
      <span className="font-mono text-xs text-muted tabular-nums" data-testid="asset-change">
        0%
      </span>
    );
  const up = bps >= 0;
  return (
    <span
      className={cn("font-mono text-xs tabular-nums", up ? "text-success" : "text-danger")}
      data-testid="asset-change"
      title="Price change in the last 24 hours"
    >
      {up ? "+" : ""}
      {formatBps(bps)}
    </span>
  );
}

function AssetRow({
  balance,
  network,
  value,
  change,
  currency,
  mask,
}: {
  balance: Balance;
  network: string;
  value: string | null;
  change: number | null;
  currency: string;
  mask: (s: string) => string;
}) {
  return (
    <li className="lg:glass min-w-0 lg:rounded-[24px]">
      <Link
        to={`/assets/${balance.chain}/${balance.asset}`}
        className="flex min-h-[4.5rem] items-center gap-3 px-4 py-3 transition-colors duration-200 hover:bg-hover lg:h-full lg:flex-col lg:items-start lg:gap-2 lg:rounded-[24px] lg:p-4"
      >
        <AssetIcon symbol={balance.asset} className="lg:size-10 lg:text-lg" />
        <div className="min-w-0 flex-1 lg:w-full lg:flex-none">
          <p className="flex items-center gap-2 text-base font-semibold text-fg lg:text-sm">
            <span className="truncate">{balance.name}</span>
            <Badge>{balance.asset}</Badge>
          </p>
          <p className="truncate text-sm text-muted lg:text-xs">{network}</p>
        </div>
        <div className="flex min-w-0 flex-col items-end gap-0.5 text-right lg:w-full lg:items-start lg:text-left">
          <span
            className="font-mono text-sm font-medium text-fg tabular-nums lg:text-lg"
            data-testid="asset-value"
          >
            {value === null ? "—" : mask(formatFiat(value, currency))}
          </span>
          <span className="max-w-full truncate font-mono text-xs text-muted tabular-nums">
            {mask(formatAmount(balance.balance))} {balance.asset}
          </span>
          <ChangeChip bps={change} />
        </div>
      </Link>
    </li>
  );
}
